/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */
/** @typedef {import('./floorPlanDesignerState.js').DesignerWall} DesignerWall */
/** @typedef {import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph} DesignerGraph */

import { directedEdgeKey, findClosedPolygons } from './floorPlanDesignerGeometry.js';
import {
  buildBoundaryWallBundle,
  mirrorBoundaryEdge,
} from './floorPlanDesignerBoundaryWalls.js';
import { buildPivotAdjacency, toDesignerGraph } from './floorPlanDesignerPivotWallPolygons.js';
import { getRoomTopologyId } from './floorPlanDesignerRooms.js';

/**
 * @typedef {'forward' | 'reverse'} BoundaryDirection
 */

/**
 * @typedef {{
 *   boundaryWallId: string;
 *   roomId: string;
 *   physicalWallId: string;
 *   startPointId: string;
 *   endPointId: string;
 *   direction: BoundaryDirection;
 *   isCapDuplicate?: boolean;
 * }} RoomBoundaryWall
 */

/**
 * @typedef {{
 *   id: string;
 *   boundaryWallIds: string[];
 *   polygonPoints: import('./floorPlanDesignerGeometry.js').Point[];
 *   wallIds: string[];
 *   pointIds: string[];
 *   areaSqM: number;
 *   polygon: import('./floorPlanDesignerGeometry.js').Point[];
 *   boundaryEdges: Array<{ wallId: string; fromPointId: string; toPointId: string }>;
 * }} DetectedRoom
 */

/**
 * @param {string} roomId
 * @param {{ wallId: string; fromPointId: string; toPointId: string }} edge
 * @returns {string}
 */
export function makeBoundaryWallId(roomId, edge) {
  return `rbw_${roomId}_${directedEdgeKey(edge.wallId, edge.fromPointId, edge.toPointId)}`;
}

/**
 * @param {DesignerWall | undefined} physicalWall
 * @param {{ wallId: string; fromPointId: string; toPointId: string }} edge
 * @returns {BoundaryDirection}
 */
export function boundaryDirectionForEdge(physicalWall, edge) {
  if (
    physicalWall &&
    physicalWall.startPointId === edge.fromPointId &&
    physicalWall.endPointId === edge.toPointId
  ) {
    return 'forward';
  }
  return 'reverse';
}

/**
 * @param {string} physicalWallId
 * @param {string} roomId
 * @param {string} terminatorWallId
 * @returns {string}
 */
export function makeCapBoundaryWallId(physicalWallId, roomId, terminatorWallId) {
  return `rbw_${roomId}_${physicalWallId}:cap:${terminatorWallId}`;
}

/**
 * @param {DesignerState | DesignerGraph} input
 * @returns {{
 *   physicalWalls: DesignerWall[];
 *   roomBoundaryWalls: RoomBoundaryWall[];
 *   rooms: DetectedRoom[];
 *   boundaryWallById: Map<string, RoomBoundaryWall>;
 *   tJunctions: import('./floorPlanDesignerBoundaryWalls.js').TJunctionInfo[];
 * }}
 */
export function buildRoomBoundaryModel(input) {
  const isDesignerState =
    input != null &&
    typeof input === 'object' &&
    'canvasWidth' in input &&
    'scale' in input &&
    'openings' in input;
  const graph = isDesignerState
    ? toDesignerGraph(/** @type {DesignerState} */ (input))
    : /** @type {DesignerGraph} */ (input);
  const stateForFaces = isDesignerState
    ? /** @type {DesignerState} */ (input)
    : {
        ...graph,
        canvasWidth: 1000,
        canvasHeight: 1000,
        scale: { metersPerPixel: 0.01, pixelsPerMeter: 100, source: 'graph' },
        openings: [],
        roomsMeta: {},
        activeChainPointIds: [],
        draftPreviewPoint: null,
      };

  const faces = findClosedPolygons(stateForFaces);
  const physicalWalls = graph.walls;
  const wallById = new Map(physicalWalls.map((wall) => [wall.id, wall]));

  /** @type {RoomBoundaryWall[]} */
  const roomBoundaryWalls = [];
  /** @type {Map<string, RoomBoundaryWall>} */
  const boundaryWallById = new Map();
  /** @type {DetectedRoom[]} */
  const rooms = [];

  for (const face of faces) {
    const roomId = getRoomTopologyId(face.pointIds);
    /** @type {string[]} */
    const boundaryWallIds = [];

    for (const edge of face.boundaryEdges || []) {
      const boundaryWallId = makeBoundaryWallId(roomId, edge);
      if (boundaryWallById.has(boundaryWallId)) {
        boundaryWallIds.push(boundaryWallId);
        continue;
      }
      const record = {
        boundaryWallId,
        roomId,
        physicalWallId: edge.wallId,
        startPointId: edge.fromPointId,
        endPointId: edge.toPointId,
        direction: boundaryDirectionForEdge(wallById.get(edge.wallId), edge),
      };
      roomBoundaryWalls.push(record);
      boundaryWallById.set(boundaryWallId, record);
      boundaryWallIds.push(boundaryWallId);
    }

    rooms.push({
      id: roomId,
      boundaryWallIds,
      polygonPoints: face.polygon.map((pt) => [...pt]),
      wallIds: [...face.wallIds],
      pointIds: [...face.pointIds],
      areaSqM: face.areaSqM,
      polygon: face.polygon,
      boundaryEdges: face.boundaryEdges,
    });
  }

  const adj = buildPivotAdjacency(graph);
  const { tJunctions } = buildBoundaryWallBundle(adj, faces, graph);

  for (const junction of tJunctions) {
    for (const cap of junction.capConnections) {
      const capBoundaryWallId = makeCapBoundaryWallId(
        cap.capStemWallId,
        cap.roomId,
        cap.terminatorWallId,
      );
      if (boundaryWallById.has(capBoundaryWallId)) continue;

      const mirrorEdge = mirrorBoundaryEdge(cap.forwardEdge);
      const record = {
        boundaryWallId: capBoundaryWallId,
        roomId: cap.roomId,
        physicalWallId: cap.capStemWallId,
        startPointId: mirrorEdge.fromPointId,
        endPointId: mirrorEdge.toPointId,
        direction: boundaryDirectionForEdge(wallById.get(cap.capStemWallId), {
          wallId: cap.capStemWallId,
          fromPointId: mirrorEdge.fromPointId,
          toPointId: mirrorEdge.toPointId,
        }),
        isCapDuplicate: true,
      };
      roomBoundaryWalls.push(record);
      boundaryWallById.set(capBoundaryWallId, record);

      const room = rooms.find((item) => item.id === cap.roomId);
      if (room && !room.boundaryWallIds.includes(capBoundaryWallId)) {
        room.boundaryWallIds.push(capBoundaryWallId);
      }
    }
  }

  return {
    physicalWalls,
    roomBoundaryWalls,
    rooms,
    boundaryWallById,
    tJunctions,
  };
}

/**
 * @param {DesignerState} state
 * @returns {DetectedRoom[]}
 */
export function buildDetectedRooms(state) {
  return buildRoomBoundaryModel(state).rooms;
}

/**
 * @param {RoomBoundaryWall} boundaryWall
 * @param {Map<string, import('./floorPlanDesignerWallPolygons.js').Point[] | import('./floorPlanDesignerPivotWallPolygons.js').WallPolygonEntry>} wallPolygons
 * @returns {import('./floorPlanDesignerGeometry.js').Point[] | null}
 */
export function getBoundaryWallVisualGeometry(boundaryWall, wallPolygons) {
  const entry = wallPolygons.get(boundaryWall.physicalWallId);
  if (!entry) return null;
  if (Array.isArray(entry) && entry.length > 0 && Array.isArray(entry[0])) {
    return /** @type {import('./floorPlanDesignerGeometry.js').Point[]} */ (entry[0]);
  }
  if (Array.isArray(entry) && entry.length >= 4 && typeof entry[0]?.[0] === 'number') {
    return /** @type {import('./floorPlanDesignerGeometry.js').Point[]} */ (entry);
  }
  return null;
}

/**
 * @param {DetectedRoom[]} rooms
 * @param {RoomBoundaryWall[]} roomBoundaryWalls
 * @returns {Array<{ boundaryWallId: string; wallId: string; roomId: string; directedEdgeKey: string; isMirror: boolean }>}
 */
export function getRoomBoundaryRefsFromModel(rooms, roomBoundaryWalls) {
  const byRoom = new Map(roomBoundaryWalls.map((item) => [item.boundaryWallId, item]));
  /** @type {Array<{ boundaryWallId: string; wallId: string; roomId: string; directedEdgeKey: string; isMirror: boolean }>} */
  const refs = [];

  for (const room of rooms) {
    for (const boundaryWallId of room.boundaryWallIds) {
      const bw = byRoom.get(boundaryWallId);
      if (!bw) continue;
      refs.push({
        boundaryWallId,
        wallId: bw.physicalWallId,
        roomId: bw.roomId,
        directedEdgeKey: directedEdgeKey(bw.physicalWallId, bw.startPointId, bw.endPointId),
        isMirror: Boolean(bw.isCapDuplicate),
      });
    }
  }
  return refs;
}
